/**
 * Pricing - the SINGLE source of truth for every price, plan and Stripe mapping.
 *
 * Consumed by the pricing UI, the paywall gate, Course `offers` JSON-LD, the
 * agent surface (llms.txt / API), Stripe checkout (M2, via lookup keys) and the
 * tests. Changing a price is editing ONE number below; every derived price
 * (monthly, extra seat, yearly totals, Stripe amounts) follows automatically.
 *
 * Rules (owner decisions, docs/MONETIZATION-PLAN.md + .claude/CLAUDE.md):
 * - Currency USD. Prices are always DISPLAYED per month.
 * - "Yearly" (never "Annually") is the default billing view.
 * - Monthly price = yearly-per-month x 1.17, rounded to whole dollars.
 * - Extra Team seat = 1.17 x the WORST (highest) per-seat monthly price of any
 *   paid subscription option, rounded to whole dollars.
 *
 * Pure data + pure functions only: no React, no catalog, no env. Safe to import
 * from client code, scripts, tests and (if ever needed) Convex.
 */

export const PRICING_CURRENCY = 'USD' as const

/** Monthly billing (and extra usage) costs 17% more than the yearly rate. */
export const MONTHLY_MARKUP = 1.17

export const PLAN_IDS = ['free', 'pro', 'team'] as const
export type PlanId = (typeof PLAN_IDS)[number]
export type PaidPlanId = Exclude<PlanId, 'free'>

export const BILLING_INTERVALS = ['year', 'month'] as const
export type BillingInterval = (typeof BILLING_INTERVALS)[number]

/** The billing interval every pricing surface shows first. */
export const DEFAULT_BILLING_INTERVAL: BillingInterval = 'year'

/**
 * i18n keys for plan feature bullets. The copy itself lives in the locale
 * dictionaries under `pricing.features.*`, so a plan's feature list is data
 * here and words there.
 */
export type PlanFeatureKey =
  | 'freeCourses'
  | 'allGuides'
  | 'progress'
  | 'community'
  | 'everythingInFree'
  | 'proCourses'
  | 'allCertificates'
  | 'proTemplates'
  | 'priorityQa'
  | 'everythingInPro'
  | 'teamSeats'
  | 'oneInvoice'
  | 'onboardingCall'

export type PlanDefinition = {
  id: PlanId
  /** Price per month when billed yearly, in whole USD. THE base number. */
  yearlyPricePerMonth: number
  /** Seats included in the base price. */
  seatsIncluded: number
  /** Highlighted card (accent background, "Recommended"). */
  recommended: boolean
  /** Whether this plan unlocks Pro content (Courses 3-5). */
  unlocksPro: boolean
  featureKeys: PlanFeatureKey[]
}

export const PLANS: Record<PlanId, PlanDefinition> = {
  free: {
    id: 'free',
    yearlyPricePerMonth: 0,
    seatsIncluded: 1,
    recommended: false,
    unlocksPro: false,
    featureKeys: ['freeCourses', 'allGuides', 'progress', 'community'],
  },
  pro: {
    id: 'pro',
    yearlyPricePerMonth: 29,
    seatsIncluded: 1,
    recommended: true,
    unlocksPro: true,
    featureKeys: [
      'everythingInFree',
      'proCourses',
      'allCertificates',
      'proTemplates',
      'priorityQa',
    ],
  },
  team: {
    id: 'team',
    yearlyPricePerMonth: 119,
    seatsIncluded: 5,
    recommended: false,
    unlocksPro: true,
    featureKeys: [
      'everythingInPro',
      'teamSeats',
      'oneInvoice',
      'onboardingCall',
    ],
  },
}

export const PAID_PLAN_IDS: PaidPlanId[] = ['pro', 'team']

/** Round to whole dollars (prices are whole-dollar by design). */
function roundDollars(value: number): number {
  return Math.round(value)
}

/** Price per month for a plan at a billing interval, in whole USD. */
export function pricePerMonth(
  planId: PlanId,
  interval: BillingInterval,
): number {
  const base = PLANS[planId].yearlyPricePerMonth
  return interval === 'year' ? base : roundDollars(base * MONTHLY_MARKUP)
}

/** Amount charged per billing period (12 months for yearly), in whole USD. */
export function pricePerBillingPeriod(
  planId: PlanId,
  interval: BillingInterval,
): number {
  const monthly = pricePerMonth(planId, interval)
  return interval === 'year' ? monthly * 12 : monthly
}

/** Yearly saving vs. paying monthly for 12 months, in whole USD. */
export function yearlySavings(planId: PlanId): number {
  return (
    pricePerMonth(planId, 'month') * 12 - pricePerBillingPeriod(planId, 'year')
  )
}

/** Yearly saving as a whole percentage of the monthly-billed year (e.g. 15). */
export function yearlySavingsPercent(planId: PlanId): number {
  const monthlyYear = pricePerMonth(planId, 'month') * 12
  if (monthlyYear === 0) return 0
  return Math.round((yearlySavings(planId) / monthlyYear) * 100)
}

/** Effective price per seat per month for a paid plan at an interval. */
export function pricePerSeatPerMonth(
  planId: PaidPlanId,
  interval: BillingInterval,
): number {
  return pricePerMonth(planId, interval) / PLANS[planId].seatsIncluded
}

/** The highest per-seat monthly price across every paid plan and interval. */
export function worstPerSeatMonthlyPrice(): number {
  return Math.max(
    ...PAID_PLAN_IDS.flatMap((planId) =>
      BILLING_INTERVALS.map((interval) =>
        pricePerSeatPerMonth(planId, interval),
      ),
    ),
  )
}

/**
 * Extra Team seat ("extra usage"), per seat per month in whole USD: 17% more
 * than the worst per-seat subscription price. Billed on the Team plan's
 * interval (monthly, or 12x per year on a yearly Team plan).
 */
export function extraSeatPricePerMonth(): number {
  return roundDollars(worstPerSeatMonthlyPrice() * MONTHLY_MARKUP)
}

/**
 * Stripe Price lookup keys (M2 resolves these to Price ids at runtime, so no
 * Stripe id is ever hardcoded). Stripe requires every item of a subscription to
 * share one interval, so the extra seat exists for both Team intervals.
 */
export const STRIPE_LOOKUP_KEYS = {
  pro_yearly: { planId: 'pro', interval: 'year', kind: 'plan' },
  pro_monthly: { planId: 'pro', interval: 'month', kind: 'plan' },
  team_yearly: { planId: 'team', interval: 'year', kind: 'plan' },
  team_monthly: { planId: 'team', interval: 'month', kind: 'plan' },
  team_extra_seat_yearly: {
    planId: 'team',
    interval: 'year',
    kind: 'extra_seat',
  },
  team_extra_seat_monthly: {
    planId: 'team',
    interval: 'month',
    kind: 'extra_seat',
  },
} as const satisfies Record<
  string,
  { planId: PaidPlanId; interval: BillingInterval; kind: 'plan' | 'extra_seat' }
>

export type StripeLookupKey = keyof typeof STRIPE_LOOKUP_KEYS

/** Lookup key for a paid plan at an interval. */
export function stripeLookupKey(
  planId: PaidPlanId,
  interval: BillingInterval,
): StripeLookupKey {
  return `${planId}_${interval === 'year' ? 'yearly' : 'monthly'}`
}

/** Lookup key for an extra Team seat at the Team plan's interval. */
export function extraSeatLookupKey(interval: BillingInterval): StripeLookupKey {
  return interval === 'year'
    ? 'team_extra_seat_yearly'
    : 'team_extra_seat_monthly'
}

/**
 * The amount Stripe must charge per period for a lookup key, in CENTS. M2's
 * Stripe setup script and tests use this to verify the Stripe Prices match the
 * config exactly.
 */
export function stripeUnitAmountCents(key: StripeLookupKey): number {
  const entry = STRIPE_LOOKUP_KEYS[key]
  const months = entry.interval === 'year' ? 12 : 1
  const perMonth =
    entry.kind === 'extra_seat'
      ? extraSeatPricePerMonth()
      : pricePerMonth(entry.planId, entry.interval)
  return perMonth * months * 100
}

/** Resolve a lookup key back to plan + interval (webhook side, M2). */
export function planFromLookupKey(
  key: string,
): {
  planId: PaidPlanId
  interval: BillingInterval
  kind: 'plan' | 'extra_seat'
} | null {
  return key in STRIPE_LOOKUP_KEYS
    ? STRIPE_LOOKUP_KEYS[key as StripeLookupKey]
    : null
}

/** Format a whole-dollar USD amount for display ("$29"). */
export function formatPrice(amount: number, locale = 'en'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: PRICING_CURRENCY,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(amount)
}

/** Whether a plan unlocks the Pro courses. */
export function planUnlocksPro(planId: PlanId): boolean {
  return PLANS[planId].unlocksPro
}

/* ---------------------------- Plan comparison ----------------------------- */

/**
 * Rows of the plan comparison table on /pricing. Data here, words in the
 * dictionaries (`pricing.compare.rows.*` and `pricing.compare.values.*`).
 * A cell is `true` (included), `false` (not included) or a value key.
 */
export type ComparisonRowKey =
  | 'freeCourses'
  | 'proCourses'
  | 'guides'
  | 'progress'
  | 'community'
  | 'certificates'
  | 'templates'
  | 'priorityQa'
  | 'seats'
  | 'invoice'
  | 'onboarding'
  | 'guarantee'

export type ComparisonValueKey =
  | 'previewOnly'
  | 'courses12'
  | 'allCourses'
  | 'oneSeat'
  | 'teamSeats'
  | 'notNeeded'

export type ComparisonCell = boolean | ComparisonValueKey

export const PLAN_COMPARISON: Array<{
  key: ComparisonRowKey
  cells: Record<PlanId, ComparisonCell>
}> = [
  { key: 'freeCourses', cells: { free: true, pro: true, team: true } },
  {
    key: 'proCourses',
    cells: { free: 'previewOnly', pro: true, team: true },
  },
  { key: 'guides', cells: { free: true, pro: true, team: true } },
  { key: 'progress', cells: { free: true, pro: true, team: true } },
  { key: 'community', cells: { free: true, pro: true, team: true } },
  {
    key: 'certificates',
    cells: { free: 'courses12', pro: 'allCourses', team: 'allCourses' },
  },
  { key: 'templates', cells: { free: false, pro: true, team: true } },
  { key: 'priorityQa', cells: { free: false, pro: true, team: true } },
  {
    key: 'seats',
    cells: { free: 'oneSeat', pro: 'oneSeat', team: 'teamSeats' },
  },
  { key: 'invoice', cells: { free: false, pro: false, team: true } },
  { key: 'onboarding', cells: { free: false, pro: false, team: true } },
  {
    key: 'guarantee',
    cells: { free: 'notNeeded', pro: true, team: true },
  },
]

/** Monthly price of a Team plan with `seats` seats at an interval, whole USD. */
export function teamPricePerMonth(
  seats: number,
  interval: BillingInterval,
): number {
  const extra = Math.max(0, Math.floor(seats) - PLANS.team.seatsIncluded)
  return pricePerMonth('team', interval) + extra * extraSeatPricePerMonth()
}

/** Upper bound for Team seats bought in one checkout (larger teams: contact). */
export const TEAM_MAX_SEATS = 100

/**
 * `{token}` values for pricing copy (FAQ answers etc.), so prose never
 * hardcodes a number that lives in this config.
 */
export function pricingCopyValues(locale = 'en') {
  return {
    yearlyPrice: formatPrice(pricePerMonth('pro', 'year'), locale),
    yearlyTotal: formatPrice(pricePerBillingPeriod('pro', 'year'), locale),
    monthlyPrice: formatPrice(pricePerMonth('pro', 'month'), locale),
    percent: yearlySavingsPercent('pro'),
    extraSeat: formatPrice(extraSeatPricePerMonth(), locale),
    teamSeats: PLANS.team.seatsIncluded,
  }
}
